/**
 * AquasealRepairCardRow — Concrete-tile sibling of FlashingRepairCardRow.
 *
 * Iter222 — the Concrete repairs builder uses a single Aquaseal catalogue
 * (`options[]`) with a `use_lead` flag instead of the Iron/Tin-Tile
 * Btray + Dektite combo. This row component mirrors the
 * Components-form card layout so all 3 repair builders share the same
 * visual paradigm, while keeping the Concrete-specific data model.
 *
 * Layout:
 *   F-chip (pin_id, amber palette) | SourceBadge (📐 from Diagram)
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Action chip (Leave/Replace) + Confirm pill                    │
 *   │ Type read-only │ Ø mm │ Qty │ Lead toggle │ Matched rate row │ │
 *   │ Fit chip + Customer/Cost                                      │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Pricing: line_total = qty × matched.rate_each (if action='replace' AND
 * confirmed AND a rate matched). Profit not surfaced (no contractor
 * rate exposed in the Concrete Aquaseal catalogue).
 */
import React from 'react';
import SourceBadge from '@/components/shared/ComponentRow/SourceBadge';
import RepairActionChip from '@/components/shared/ComponentRow/RepairActionChip';
import ConfirmRequiredBanner from '@/components/shared/ComponentRow/ConfirmRequiredBanner';
import RateDetailEditButton from '@/components/RateDetailEditButton';
import EditRateLink from '@/components/EditRateLink';
import { fmt } from '@/components/shared/ComponentRow/helpers';

// Concrete repair section palette — amber/orange-ish so the F-chip
// reads visually distinct from the green Iron flashing chips.
const AQUASEAL_COLOUR = '#B45309';

const FIT_TONES = {
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800' },
  rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-800' },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-800' },
  slate:   { bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-500' },
};

function computeFit(diameter_mm, matched) {
  if (!diameter_mm || !matched) return { tone: 'slate', label: '—', title: 'No pipe diameter set' };
  const lo = matched.size_min_mm || matched.size_mm || 0;
  const hi = matched.size_max_mm || matched.size_mm || 0;
  const d = parseFloat(diameter_mm);
  if (lo && hi && d >= lo && d <= hi) {
    return { tone: 'emerald', label: `fits ${lo}–${hi}mm`, title: `Ø${d}mm falls within the ${matched.name} rated range ${lo}–${hi}mm` };
  }
  if (lo && hi) {
    return { tone: 'rose', label: `outside ${lo}–${hi}mm`, title: `Ø${d}mm does NOT fit the ${matched.name} range ${lo}–${hi}mm — pick a different Dektite size or split into two pins` };
  }
  if (matched.size_mm) {
    const tol = 10;
    const diff = Math.abs(d - matched.size_mm);
    if (diff <= tol) {
      return { tone: 'emerald', label: `~${matched.size_mm}mm exact`, title: `Ø${d}mm matches the ${matched.size_mm}mm Dektite (within ${tol}mm)` };
    }
    return { tone: 'amber', label: `±${Math.round(diff)}mm off`, title: `Ø${d}mm differs from the matched ${matched.size_mm}mm by ${Math.round(diff)}mm — confirm next-size-up coverage` };
  }
  return { tone: 'slate', label: '—', title: 'No size range on the matched rate row' };
}

function matchAquaseal(diameter_mm, options, use_lead) {
  if (!options || !options.length) return null;
  const candidates = options.filter(o => (o.material === 'lead') === !!use_lead);
  const ranked = (candidates.length ? candidates : options).slice().sort((x, y) => (
    ((x.rate_each || 0) > 0 ? 0 : 1) - ((y.rate_each || 0) > 0 ? 0 : 1)
    || Math.abs((x.size_mm || 0) - (diameter_mm || 0)) - Math.abs((y.size_mm || 0) - (diameter_mm || 0))
  ));
  return ranked[0] || null;
}

export default function AquasealRepairCardRow({
  refId,
  testidPrefix,
  pinId,
  pinType = '',
  diameterMm = null,
  qty = 0,
  useLead = false,
  action = 'replace',
  confirmed = true,
  options = [],
  onUpdate,
  onAction,
  onConfirm,
  onUnconfirm,
  onJumpToPin,
}) {
  const isLeave = (action || 'replace').toLowerCase() === 'leave';
  const matched = matchAquaseal(diameterMm, options, useLead);
  const fit = computeFit(diameterMm, matched);
  const fitClass = FIT_TONES[fit.tone] || FIT_TONES.slate;
  const includesInTotal = !isLeave && confirmed && matched;
  const lineTotal = includesInTotal ? (Number(qty) || 0) * (Number(matched.rate_each) || 0) : 0;
  const costToUs = lineTotal * 0.65;

  return (
    <div
      className={`bg-white border rounded-md p-3 pl-12 grid grid-cols-12 gap-2 items-start relative ${
        !confirmed ? 'border-amber-300 bg-amber-50/30'
        : isLeave ? 'border-slate-200 bg-slate-50/40'
        : 'border-[#E2E2DF]'
      }`}
      data-testid={`${testidPrefix}-card`}
      data-pending={!confirmed ? '1' : '0'}
      data-action={action}
    >
      <span
        data-testid={`${testidPrefix}-ref`}
        className="absolute left-2 top-3 inline-flex items-center justify-center min-w-[34px] px-1.5 py-1 rounded-md text-[10px] font-bold tracking-wide font-mono-num text-white shadow-sm"
        style={{ background: AQUASEAL_COLOUR }}
        title={`Pin ${pinId}`}
      >
        {refId}
      </span>

      <SourceBadge line={{ import_source: 'roof_diagram' }} />

      {!confirmed && (
        <ConfirmRequiredBanner
          confirmed={false}
          onConfirm={onConfirm}
          testid={`${testidPrefix}-confirm`}
          variant="banner"
        />
      )}

      {/* Action + (when confirmed) compact ✓ pill */}
      <div className="col-span-12 md:col-span-2">
        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 flex items-center gap-1">
          Action
          {confirmed && (
            <ConfirmRequiredBanner
              confirmed
              onUnconfirm={onUnconfirm}
              testid={`${testidPrefix}-confirm`}
            />
          )}
        </label>
        <RepairActionChip
          value={isLeave ? 'leave' : 'replace'}
          onChange={onAction}
          testid={`${testidPrefix}-action`}
          disableUpgrade
        />
      </div>

      {/* Type (pipe type from diagram, read-only) */}
      <div className="col-span-6 md:col-span-2">
        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Type</label>
        <div
          data-testid={`${testidPrefix}-type`}
          className="w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md bg-slate-50 text-slate-700 truncate"
          title={pinType || 'From diagram pin'}
        >
          {pinType || '—'}
        </div>
        {onJumpToPin && (
          <button
            type="button"
            data-testid={`${testidPrefix}-jump`}
            onClick={() => onJumpToPin(pinId, pinType)}
            className="mt-1 text-[10px] text-amber-700 hover:text-amber-900 underline-offset-2 hover:underline"
            title="Highlight this pin on the diagram"
          >Jump to {pinId} ↗</button>
        )}
      </div>

      {/* Diameter */}
      <div className="col-span-6 md:col-span-1">
        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Ø mm</label>
        <div
          data-testid={`${testidPrefix}-diameter`}
          className="w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md bg-slate-50 text-right font-mono-num text-slate-700"
        >
          {diameterMm || '—'}
        </div>
      </div>

      {/* Qty */}
      <div className="col-span-6 md:col-span-1">
        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Qty</label>
        <input
          type="number" step="1" min="0"
          data-testid={`${testidPrefix}-qty`}
          value={qty || 0}
          disabled={isLeave}
          onChange={(e) => onUpdate?.({ qty: parseInt(e.target.value, 10) || 0 })}
          className={`w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md font-mono-num text-right ${
            isLeave ? 'bg-slate-100 text-slate-400' : ''
          }`}
        />
      </div>

      {/* Lead toggle */}
      <div className="col-span-6 md:col-span-1">
        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Lead</label>
        <label
          className={`inline-flex items-center gap-1 text-xs px-2 py-1.5 border rounded-md cursor-pointer transition-colors ${
            isLeave ? 'opacity-50 cursor-not-allowed bg-slate-50 border-slate-200'
            : useLead ? 'bg-amber-100 border-amber-300 text-amber-900'
            : 'bg-white border-[#E2E2DF] text-slate-600 hover:border-amber-300'
          }`}
          title={isLeave ? '' : 'Tick to use a lead-based Dektite. Affects which catalogue rate row is matched (and the price).'}
        >
          <input
            type="checkbox"
            data-testid={`${testidPrefix}-lead`}
            checked={!!useLead}
            onChange={(e) => onUpdate?.({ use_lead: e.target.checked })}
            disabled={isLeave}
            className="rounded"
          />
          <span>Lead</span>
        </label>
      </div>

      {/* Matched rate row + Fit chip */}
      <div className="col-span-12 md:col-span-3">
        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Matched rate · Fit</label>
        <div className="text-[11px] flex flex-col gap-1">
          {isLeave ? (
            <span className="text-slate-400 italic px-2 py-1">—</span>
          ) : (
            <>
              {matched ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-slate-50 border border-slate-200 rounded">
                  <span className="text-slate-700">{matched.name}</span>
                  <span className="text-slate-500 font-mono-num">·  ${(Number(matched.rate_each) || 0).toFixed(2)} ea</span>
                  {matched.id && (
                    <>
                      <RateDetailEditButton
                        component="flashing"
                        rate={matched}
                        testid={`${testidPrefix}-match-detail`}
                      />
                      <EditRateLink component="aquaseal" rateId={matched.id} testid={`${testidPrefix}-match-edit`} label="✎" />
                    </>
                  )}
                </span>
              ) : (
                <span className="px-2 py-1 text-rose-700 italic">— no rate matched —</span>
              )}
              <span
                data-testid={`${testidPrefix}-fit`}
                className={`text-[10px] px-1.5 py-0.5 rounded font-semibold border ${fitClass.bg} ${fitClass.border} ${fitClass.text} self-start`}
                title={fit.title}
              >
                {fit.label}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Customer / Cost */}
      <div className="col-span-12 md:col-span-2">
        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Customer / Cost</label>
        <div
          data-testid={`${testidPrefix}-customer-cost`}
          className="flex items-baseline gap-2 px-2 py-1.5 bg-slate-50 rounded-md border border-[#E2E2DF]"
        >
          <span className="text-sm font-bold text-slate-900 font-mono-num">{fmt(lineTotal)}</span>
          <span className="text-[10px] text-slate-400 font-mono-num">cost {fmt(costToUs)}</span>
          {includesInTotal ? (
            <span className="ml-auto text-[10px] font-bold font-mono-num text-emerald-700">+{fmt(lineTotal - costToUs)}</span>
          ) : (
            <span className="ml-auto text-[10px] text-slate-400">—</span>
          )}
        </div>
      </div>
    </div>
  );
}
